// src/components/Dashboard/Dashboard.js

import React, { useState, useEffect } from "react";
import { auth } from "../../firebaseConfig";
import { AI_ENABLED } from '../../config/constants';
import "./Dashboard.css";

const PROJECT_KEY = "ijrr_user";
const ROOT_API_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_FLAG = "/assets/images/defaults/flag_white.png";

export default function Dashboard() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Avatar and Flag states
  const [mainAssetThumb, setMainAssetThumb] = useState(null);
  const [userFlag, setUserFlag] = useState(DEFAULT_FLAG);
  const [validPhoto, setValidPhoto] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        // Fetch all necessary data in parallel
        fetchCustomerData(firebaseUser.uid);
        fetchUserMainAsset(firebaseUser.uid);
        fetchUserMembership(firebaseUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchCustomerData = async (uid) => {
    try {
      const res = await fetch(`${ROOT_API_URL}/customers/${uid}`);
      if (!res.ok) throw new Error("Failed to fetch user data");

      const data = await res.json();
      setUserData(data.customer);
      localStorage.setItem(PROJECT_KEY, JSON.stringify(data.customer));
    } catch (err) {
      console.error("❌ Error fetching user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserMainAsset = async (firebaseUid) => {
    try {
      const response = await fetch(`${ROOT_API_URL}/customer-assets/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebase_uid: firebaseUid })
      });

      if (response.ok) {
        const data = await response.json();
        const mainAsset = data.assets.find(asset => asset.is_main);
        
        if (mainAsset) {
          setMainAssetThumb(mainAsset.thumbnail_url || mainAsset.url);
        } else {
          setMainAssetThumb(null);
        }
      }
    } catch (error) {
      console.error("Error fetching user assets:", error);
      setMainAssetThumb(null);
    }
  };

  /**
   * NEW: Fetches the membership to get the custom flag design
   */
  const fetchUserMembership = async (firebaseUid) => {
    try {
      const response = await fetch(`${ROOT_API_URL}/memberships/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebase_uid: firebaseUid })
      });

      if (response.ok) {
        const data = await response.json();
        // If the user has a membership, use the custom flag design
        if (data.memberships && data.memberships.length > 0) {
          setUserFlag(data.memberships[0].flag_design);
        } else {
          setUserFlag(DEFAULT_FLAG);
        }
      }
    } catch (error) {
      console.error("Error fetching user membership:", error);
      setUserFlag(DEFAULT_FLAG);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : "";
    const last = lastName ? lastName.charAt(0).toUpperCase() : "";
    return (first + last) || "U";
  };

  return (
    <section className="dashboard-section space-pb-80">
      <div className="container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            <div className="dashboard-header">
              <div className="dashboard-avatar-info">
                
                {mainAssetThumb ? (
                    <img
                        src={mainAssetThumb}
                        alt="User Avatar"
                        className="user-avatar"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            setMainAssetThumb(null);
                        }}
                    />
                ) : (userData?.avatar && validPhoto) ? (
                  <img
                    src={userData.avatar}
                    alt="User Avatar"
                    className="user-avatar"
                    onError={() => setValidPhoto(false)}
                  />
                ) : (
                  <div className="default-avatar">
                    {getInitials(userData?.first_name, userData?.last_name)}
                  </div>
                )}

                <div className="dashboard-user-info">
                  <h2 className="user-name">
                    {userData?.first_name} {userData?.last_name}
                  </h2>
                  <p className="user-email">{userData?.email}</p>
                  
                  {AI_ENABLED && (
                    <p className="pirate-prompt">🏴‍☠️ “Captain of the Cursed Cuy Fleet”</p>
                  )}
                </div>
              </div>

              {AI_ENABLED && (
                <div className="main-logo-preview">
                  <img
                    src={mainAssetThumb || "/assets/images/dashboard/ai-logo.png"}
                    alt="Main Pirate Logo"
                    className="main-logo"
                    style={{ objectFit: 'contain' }}
                  />
                  <p className="logo-caption">{mainAssetThumb ? "Your current main asset" : "No main asset selected"}</p>
                </div>
              )}
            </div>

            <div className="dashboard-panels">
              <div className="panel">
                <h3>My Flag</h3>
                {/* UPDATED: Displays custom flag design or default white flag */}
                <img
                  src={userFlag}
                  alt="User Flag"
                  className="flag-img"
                  onError={() => setUserFlag(DEFAULT_FLAG)}
                />
                <p>{userFlag === DEFAULT_FLAG ? "Default White Flag" : "Official Registered Flag"}</p>
              </div>

              <div className="panel">
                <h3>Orders</h3>
                <p>Status: <strong>Shipped</strong></p>
                <p>ETA: Feb 17, 2026</p>
                <a href="/shopping-cart" className="panel-link">View Order</a>
              </div>

              <div className="panel">
                <h3>Profile</h3>
                <p>Edit your pirate info</p>
                <a href="/profile" className="panel-link">Go to Profile</a>
              </div>

              {AI_ENABLED && (
                <div className="panel">
                  <h3>Redesigns</h3>
                  <p>You haven't made any redesigns yet.</p>
                  <a href="/ijrr-gear" className="panel-link">Start Designing</a>
                </div>
              )}

              <div className="panel">
                <h3>History</h3>
                <p>Feature coming soon!</p>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}